import { useEffect } from "react";
import { Link } from "react-router-dom";

/**
 * 404 page.
 * Note: In a client-rendered SPA, the HTTP status is still 200 (edge server
 * always serves index.html). We combat this with:
 *   1. <meta name="robots" content="noindex, nofollow"> so this page won't be indexed
 *   2. A page title that explicitly says "404 Not Found"
 *   3. Clear noindex JSON-LD (or no schema)
 * True 404 status codes require a Cloudflare / server rule — see docs.
 */
export default function NotFound() {
  useEffect(() => {
    document.title = "404 Not Found | Gary Zelesky";
    const meta = document.querySelector('meta[name="robots"]');
    if (meta) {
      meta.setAttribute("data-original", meta.getAttribute("content") || "");
      meta.setAttribute("content", "noindex, nofollow");
    }
    return () => {
      const m = document.querySelector('meta[name="robots"]');
      if (m && m.getAttribute("data-original") !== null) {
        m.setAttribute("content", m.getAttribute("data-original"));
      }
    };
  }, []);

  return (
    <div
      data-testid="not-found-page"
      className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6"
    >
      <div className="max-w-2xl text-center">
        <div className="text-[#EC1985] text-xs tracking-[3px] uppercase font-bold mb-4">
          Gary Zelesky · 404
        </div>
        <h1 className="text-6xl md:text-8xl font-bold mb-6">Page Not Found</h1>
        <p className="text-white/80 text-lg mb-10 leading-relaxed">
          The page you&rsquo;re looking for doesn&rsquo;t exist &mdash; maybe it moved, maybe it never did.
          Either way, here are the places you probably meant to go:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10">
          {[
            { to: "/", label: "Home" },
            { to: "/cause-for-applause", label: "Cause for Applause" },
            { to: "/dental-keynote-speaker", label: "Dental Keynote Speaker" },
            { to: "/the-passion-centered-person", label: "The Passion Centered Person" },
          ].map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="border border-[#262626] hover:border-[#EC1985] px-4 py-3 text-left transition-colors"
              data-testid={`nf-link-${l.to.replace(/\//g, "") || "home"}`}
            >
              <span className="text-neutral-400 text-xs uppercase tracking-widest">Go to</span>
              <div className="text-white font-bold mt-1">{l.label}</div>
            </Link>
          ))}
        </div>
        <Link
          to="/#book"
          className="inline-block bg-[#EC1985] text-white px-8 py-4 font-bold uppercase tracking-widest text-sm hover:bg-[#d1176f] transition-colors"
          data-testid="nf-book-btn"
        >
          Book Gary For Your Event
        </Link>
      </div>
    </div>
  );
}
